import React, { useEffect, useState } from "react";
import axios from "axios";
import { config } from "../../../config";
import UpdateSteadfast from "./UpdateSteadfast";
import UpdatePathao from "./UpdatePathao";

const apiUrl = config.apiUrl;

const ViewCourierConfig = () => {
  const [activeTab, setActiveTab] = useState("steadfast");
  const [steadfasts, setSteadfasts] = useState([]);
  const [pathaoData, setPathaoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // EDIT STATES
  const [editingSteadfast, setEditingSteadfast] = useState(null);
  const [editingPathao, setEditingPathao] = useState(false);

  const [formDataSteadfast, setFormDataSteadfast] = useState({
    apiKey: "",
    secretKey: ""
  });

  const [formDataPathao, setFormDataPathao] = useState({
    pathao_client_id: "",
    pathao_client_secret: "",
    pathao_email: "",
    pathao_password: "",
    pathao_store_id: ""
  });

  // FETCH DATA
  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const sf = await axios.get(`${apiUrl}/steadfasts`);
        const pt = await axios.get(`${apiUrl}/pathao/settings`);
        
        setSteadfasts(sf.data);
        setPathaoData(pt.data);
        setError(null);
      } catch (err) {
        setError("Failed to load API data.");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // DELETE STEADFAST
  const handleDeleteSteadfast = async (id) => {
    if (!window.confirm("Delete Steadfast config?")) return;

    try {
      await axios.delete(`${apiUrl}/steadfastdelete/${id}`);
      setSteadfasts((prev) => prev.filter((item) => item.id !== id));
    } catch (err) {
      setError("Failed to delete Steadfast config.");
      console.error("Delete error:", err);
    }
  };

  // DELETE PATHAO
  const handleDeletePathao = async () => {
    if (!window.confirm("Delete Pathao config?")) return;

    try {
      await axios.delete(`${apiUrl}/pathao/delete`);
      setPathaoData(null);
    } catch (err) {
      setError("Failed to delete Pathao config.");
      console.error("Delete error:", err);
    }
  };

  // HANDLE EDIT CLICK (STEADFAST)
  const handleEditSteadfast = (item) => {
    setEditingSteadfast(item.id);
    setFormDataSteadfast({
      apiKey: item.apiKey || "",
      secretKey: item.secretKey || ""
    });
  };

  // HANDLE EDIT CLICK (PATHAO)
  const handleEditPathao = () => {
    if (pathaoData) {
      setEditingPathao(true);
      setFormDataPathao({
        pathao_client_id: pathaoData.pathao_client_id || "",
        pathao_client_secret: pathaoData.pathao_client_secret || "",
        pathao_email: pathaoData.pathao_email || "",
        pathao_password: pathaoData.pathao_password || "",
        pathao_store_id: pathaoData.pathao_store_id || ""
      });
    }
  };

  // HANDLE CANCEL EDIT
  const handleCancelSteadfast = () => {
    setEditingSteadfast(null);
    setFormDataSteadfast({ apiKey: "", secretKey: "" });
  };

  const handleCancelPathao = () => {
    setEditingPathao(false);
    setFormDataPathao({
      pathao_client_id: "",
      pathao_client_secret: "",
      pathao_email: "",
      pathao_password: "",
      pathao_store_id: ""
    });
  };

  // HANDLE UPDATE SUCCESS
  const handleUpdateSteadfastSuccess = (updatedData) => {
    setSteadfasts(prev => 
      prev.map(item => 
        item.id === editingSteadfast ? { ...item, ...updatedData } : item
      )
    );
    setEditingSteadfast(null);
    setFormDataSteadfast({ apiKey: "", secretKey: "" });
  };

  const handleUpdatePathaoSuccess = (updatedData) => {
    setPathaoData(updatedData);
    setEditingPathao(false);
    setFormDataPathao({
      pathao_client_id: "",
      pathao_client_secret: "",
      pathao_email: "",
      pathao_password: "",
      pathao_store_id: ""
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Tabs */}
      <div className="border-b mb-6">
        <nav className="flex space-x-6">
          <button
            className={`pb-2 px-1 font-medium transition-colors ${
              activeTab === "steadfast" 
                ? "border-b-2 border-blue-600 text-blue-600" 
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("steadfast")}
          >
            Steadfast
          </button>
          <button
            className={`pb-2 px-1 font-medium transition-colors ${
              activeTab === "pathao" 
                ? "border-b-2 border-blue-600 text-blue-600" 
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("pathao")}
          >
            Pathao
          </button>
        </nav>
      </div>

      {/* ERROR */}
      {error && (
        <div className="mb-6 p-3 bg-red-100 text-red-600 border border-red-300 rounded">
          {error}
        </div>
      )}

      {/* STEADFAST TAB */}
      {activeTab === "steadfast" && (
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold mb-4">Steadfast Credentials</h2>

          {steadfasts.length === 0 ? (
            <p className="text-gray-500 text-center py-6">No Steadfast data found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">API Key</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Secret Key</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {steadfasts.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">{item.apiKey}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{item.secretKey}</td>
                      <td className="px-4 py-3 text-sm space-x-3">
                        <button
                          onClick={() => handleEditSteadfast(item)}
                          className="text-blue-600 hover:text-blue-800 font-medium transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteSteadfast(item.id)}
                          className="text-red-600 hover:text-red-800 font-medium transition-colors"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Update Steadfast Modal */}
          {editingSteadfast && (
            <UpdateSteadfast
              formData={formDataSteadfast}
              setFormData={setFormDataSteadfast}
              onCancel={handleCancelSteadfast}
              onSuccess={handleUpdateSteadfastSuccess}
              id={editingSteadfast}
            />
          )}
        </div>
      )}

      {/* PATHAO TAB */}
      {activeTab === "pathao" && (
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold mb-4">Pathao Credentials</h2>

          {!pathaoData ? (
            <p className="text-gray-500 py-6 text-center">No Pathao credentials found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client Secret</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Store ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{pathaoData.pathao_client_id}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">••••••••</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{pathaoData.pathao_email}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{pathaoData.pathao_store_id}</td>
                    <td className="px-4 py-3 text-sm space-x-3">
                      <button
                        onClick={handleEditPathao}
                        className="text-blue-600 hover:text-blue-800 font-medium transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={handleDeletePathao}
                        className="text-red-600 hover:text-red-800 font-medium transition-colors"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Update Pathao Modal */}
          {editingPathao && (
            <UpdatePathao
              formData={formDataPathao}
              setFormData={setFormDataPathao}
              onCancel={handleCancelPathao}
              onSuccess={handleUpdatePathaoSuccess}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ViewCourierConfig;